/**
 * 로그인 «돌아갈 곳» 단일 출처 (signup-fix-1031 S-05/S-06/S-07).
 *
 * 콜백(route.ts)·로그인 페이지(page.tsx)·LoginClient·온보딩·미들웨어·헤더 로그인 링크가
 * 각자 검사하던 것을 한 곳으로 모았다. 순수 문자열 모듈 — Edge(미들웨어)·클라이언트 모두 안전.
 *
 * 규칙: 같은 출처의 «경로» 만 허용한다. 절대 URL·프로토콜 상대(//)·역슬래시(/\)·
 * 선행 공백/제어문자·백슬래시 포함 경로는 전부 거부하고 fallback('/')으로 보낸다.
 */

/** 이어서 할 동작(로그인 후 자동 재개). 화이트리스트 밖 값은 버린다. */
export const RESUME_ACTIONS = ['register_interest', 'bookmark', 'comment', 'empathy'] as const;
export type ResumeAction = (typeof RESUME_ACTIONS)[number];

export function isSafeInternalPath(p: unknown): p is string {
  if (typeof p !== 'string' || p.length === 0 || p.length > 1500) return false;
  if (!p.startsWith('/')) return false;
  if (p.startsWith('//') || p.startsWith('/\\')) return false;
  if (/^\/[\t\r\n\v\f ]/.test(p)) return false;
  // 경로 어디에든 역슬래시·제어문자가 있으면 브라우저가 // 로 해석할 여지가 있다
  if (/[\\\u0000-\u001f\u007f]/.test(p)) return false;
  return true;
}

/** 안전하지 않으면 fallback. */
export function safeReturnPath(p: unknown, fallback = '/'): string {
  return isSafeInternalPath(p) ? p : fallback;
}

export function safeResumeAction(a: unknown): ResumeAction | null {
  return typeof a === 'string' && (RESUME_ACTIONS as readonly string[]).includes(a) ? (a as ResumeAction) : null;
}

/** source 는 계측 키(apt_interest_<slug> 포함) — 제어문자 제거·길이 상한. 인코딩은 빌더가 한다. */
export function safeSource(s: unknown): string | null {
  if (typeof s !== 'string') return null;
  const t = s.replace(/[\u0000-\u001f\u007f]/g, '').trim().slice(0, 120);
  return t ? t : null;
}

/** 현재 위치 → 돌아갈 경로(쿼리 보존). 로그인 직후 붙는 일회성 파라미터는 걷는다. */
export function currentReturnPath(pathname: string, search = ''): string {
  let q = search.startsWith('?') ? search.slice(1) : search;
  if (q) {
    const sp = new URLSearchParams(q);
    for (const k of ['welcome', 'kd_auth', 'kd_resume']) sp.delete(k);
    q = sp.toString();
  }
  return safeReturnPath(q ? `${pathname}?${q}` : pathname);
}

export interface LoginTarget {
  redirect?: string | null;
  source?: string | null;
  action?: string | null;
}

function targetQuery(t: LoginTarget): string {
  const sp = new URLSearchParams();
  sp.set('redirect', safeReturnPath(t.redirect));
  const src = safeSource(t.source);
  if (src) sp.set('source', src);
  const act = safeResumeAction(t.action);
  if (act) sp.set('action', act);
  return sp.toString();
}

/** /login?redirect=…&source=…&action=… (모두 인코딩). */
export function buildLoginHref(t: LoginTarget): string {
  return `/login?${targetQuery(t)}`;
}

/** OAuth redirectTo — `${origin}/auth/callback?redirect=…&source=…&action=…`. */
export function buildCallbackUrl(origin: string, t: LoginTarget): string {
  return `${origin}/auth/callback?${targetQuery(t)}`;
}

/** 실패·취소 시 로그인 화면으로 «돌아갈 곳을 들고» 되돌린다. */
export function buildLoginRetryHref(errorCode: string, t: LoginTarget): string {
  const sp = new URLSearchParams();
  sp.set('error', errorCode);
  if (errorCode !== 'cancelled') sp.set('retry', '1');
  sp.set('redirect', safeReturnPath(t.redirect));
  const src = safeSource(t.source);
  if (src) sp.set('source', src);
  const act = safeResumeAction(t.action);
  if (act) sp.set('action', act);
  return `/login?${sp.toString()}`;
}

/** 목적지에 쿼리 파라미터를 덧붙인다(기존 쿼리·해시 보존). */
export function appendQuery(path: string, params: Record<string, string | null | undefined>): string {
  const hashIdx = path.indexOf('#');
  const hash = hashIdx >= 0 ? path.slice(hashIdx) : '';
  const base = hashIdx >= 0 ? path.slice(0, hashIdx) : path;
  const qIdx = base.indexOf('?');
  const pathname = qIdx >= 0 ? base.slice(0, qIdx) : base;
  const sp = new URLSearchParams(qIdx >= 0 ? base.slice(qIdx + 1) : '');
  for (const [k, v] of Object.entries(params)) if (v != null && v !== '') sp.set(k, v);
  const q = sp.toString();
  return `${pathname}${q ? `?${q}` : ''}${hash}`;
}

/**
 * 로그인이 필요한 «계정 전용» 경로(미들웨어 보호). D9: /profile/[id] 는 공개 작성자 프로필이라 빼고,
 * 내 프로필(/profile)과 청약 가점 설정(/profile/cheongak/*)만 보호한다. 접두 일치는 세그먼트 경계로만.
 */
const LOGIN_REQUIRED_PREFIXES = ['/write', '/payment', '/notifications', '/admin', '/profile/cheongak'] as const;
export function isLoginRequiredPath(pathname: string): boolean {
  if (pathname === '/profile') return true;
  return LOGIN_REQUIRED_PREFIXES.some((p) => pathname === p || pathname.startsWith(p + '/'));
}

/** S-09: kd_auth=signup.kakao → { event:'sign_up', method:'kakao' }. 형식 밖이면 null. */
export function parseAuthEventParam(v: string | null | undefined): { event: 'sign_up' | 'login'; method: string } | null {
  if (!v) return null;
  const m = /^(signup|login)\.([a-z0-9_-]{1,20})$/.exec(v);
  if (!m) return null;
  return { event: m[1] === 'signup' ? 'sign_up' : 'login', method: m[2] };
}

/**
 * D13: 로그인 후 «이어서 할 동작» 1회 소비(클라이언트). 주소의 kd_resume 가 expected 면 지우고 true.
 * 다른 값이면 건드리지 않는다(같은 화면의 다른 컴포넌트 몫).
 */
export function consumeResumeParam(expected: ResumeAction): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.get('kd_resume') !== expected) return false;
    url.searchParams.delete('kd_resume');
    window.history.replaceState(window.history.state, '', url.pathname + (url.search || '') + url.hash);
    return true;
  } catch {
    return false;
  }
}
